/**
===================================================
Module: frontend.src.App.tsx
Description: Main application component with routing and authentication
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import ResetPassword from "./components/ResetPassword";
import VerifyEmail from "./components/VerifyEmail";
import Projects from "./components/Projects";
import Sessions from "./components/Sessions";
import PDFEditor from "./components/PDFEditor";
import AdminUsers from "./components/AdminUsers";
import AdminSettings from "./components/AdminSettings";
import AdminNotifications from "./components/AdminNotifications";
import AuditLog from "./components/AuditLog";
import TenantUsers from "./components/TenantUsers";
import TenantGroups from "./components/TenantGroups";
import TenantAuditLog from "./components/TenantAuditLog";
import CompleteProfile from "./components/CompleteProfile";
import ChangePassword from "./components/ChangePassword";
import { hasRole, TenantRole } from "./types/tenant";
import { useTranslation } from "react-i18next";
import "./styles/variables.css"; // Import global variables first
import "./App.css";

/** Signed in, with no further requirement. Used by the profile-completion
 *  screen itself, which must stay reachable while the profile is incomplete. */
const AuthedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="loading-container">
        {t('loading')}
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="loading-container">
        {t('loading')}
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.profile_completed === false) {
    return <Navigate to="/profile/complete" replace />;
  }
  if (user?.password_change_required) {
    return <Navigate to="/password/change" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="loading-container">
        {t('loading')}
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.profile_completed === false) {
    return <Navigate to="/profile/complete" replace />;
  }
  if (user?.password_change_required) {
    return <Navigate to="/password/change" replace />;
  }
  if (!user?.is_superuser) return <Navigate to="/projects" />;

  return <>{children}</>;
};

/** Gates a route on the caller's role inside their own tenant. */
const TenantRoute: React.FC<{
  children: React.ReactNode;
  minimum: TenantRole;
}> = ({ children, minimum }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="loading-container">
        {t('loading')}
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.profile_completed === false) {
    return <Navigate to="/profile/complete" replace />;
  }
  if (user?.password_change_required) {
    return <Navigate to="/password/change" replace />;
  }
  if (!hasRole(user?.tenant_role ?? undefined, minimum)) {
    return <Navigate to="/projects" />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/profile/complete"
              element={
                <AuthedRoute>
                  <CompleteProfile />
                </AuthedRoute>
              }
            />
            <Route
              path="/password/change"
              element={
                <AuthedRoute>
                  <ChangePassword />
                </AuthedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <Projects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/sessions"
              element={
                <ProtectedRoute>
                  <Sessions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions/:sessionId/results"
              element={
                <ProtectedRoute>
                  <PDFEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminRoute>
                  <AdminSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <AdminRoute>
                  <AdminNotifications />
                </AdminRoute>
              }
            />
            <Route path="/admin" element={<Navigate to="/admin/users" />} />
            <Route
              path="/team"
              element={
                <TenantRoute minimum="admin">
                  <TenantUsers />
                </TenantRoute>
              }
            />
            <Route
              path="/team/groups"
              element={
                <TenantRoute minimum="super_admin">
                  <TenantGroups />
                </TenantRoute>
              }
            />
            <Route
              path="/team/audit-logs"
              element={
                <TenantRoute minimum="super_admin">
                  <TenantAuditLog />
                </TenantRoute>
              }
            />
            <Route
              path="/account/audit-logs"
              element={
                <ProtectedRoute>
                  <AuditLog />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/projects" />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
